import { Link, useLocation } from '@remix-run/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import ChatBotWidget from '~/components/chatbot/ChatBotWidget';
import { EASE_ARRAY } from '~/core/generic/helpers';
import useWindowSize from '~/core/hooks/use-window-size';
import FancyButton from './FancyButton';
import { FooterCopyright } from './FooterCopyright';

interface FullScreenMenuProps {
  links: any;
  onToggleMenu?: () => void;
}

const FullscreenMenu: React.FC<FullScreenMenuProps> = ({
  links,
  onToggleMenu,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { height, width } = useWindowSize();
  const className =
    'text-black dark:text-white text-2xl md:text-4xl mb-4 md:mb-8 hover:!text-secondary transition-colors ease-in-out';

  const Slash = () => <span className="text-secondary mr-3">/</span>;
  const Dot = () => (
    <span className="text-secondary ml-1" style={{ fontFamily: 'sans-serif' }}>
      .
    </span>
  );

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (onToggleMenu) {
      onToggleMenu();
    }
  };

  const menuVariants = {
    open: {
      scaleY: 1,
      opacity: 1,
      transition: { ease: EASE_ARRAY, duration: 1 },
      transform: 'translateX(-50%)',
    },
    closed: {
      scaleY: 0,
      opacity: 0,
      transition: { ease: EASE_ARRAY, duration: 1 },
      transform: 'translateX(-50%)',
    },
  };

  const pathVariants = {
    open: (index: number) => ({
      scaleX: index === 1 ? 0 : 1,
      rotate: index === 0 ? -45 : index === 2 ? 45 : 0,
      translateX: index === 0 ? 0 : index === 2 ? -5 : 0,
      translateY: index === 0 ? 7 : index === 2 ? 2 : 0,
      transition: { ease: 'easeInOut', duration: 0.3 },
    }),
    closed: () => ({
      scaleX: 1,
      rotate: 0,
      translateX: 0,
      translateY: 0,
      transition: { ease: 'easeInOut', duration: 0.3 },
    }),
  };

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (isOpen) {
        document.body.setAttribute('data-scroll-locked', '1');
        document.documentElement.setAttribute('data-scroll-locked', '1');
      } else {
        document.body.removeAttribute('data-scroll-locked');
        document.documentElement.removeAttribute('data-scroll-locked');
      }
    }
  }, [isOpen]);

  return (
    <div className="flex relative">
      <FancyButton
        dotAmount={10}
        onClick={toggleMenu}
        variant="outline"
        color="#fff"
        className="flex justify-center items-center !min-w-0 !h-[44px] md:!h-[54px] !w-[44px] md:!w-[54px] rounded-[54px] !ml-0 !p-0 !m-0 border-2 border-solid !text-[#1f2937] dark:!text-white hover:!text-[#1f2937] dark:hover:!text-[#1f2937] !border-[#1f2937] dark:!border-white [&>span]:!flex [&>span]:!justify-center"
        canvasClassName="m-0 !inset-0 h-auto w-full"
        textShadow={false}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            d="M3 6h18"
            variants={pathVariants}
            custom={0}
            animate={isOpen ? 'open' : 'closed'}
          />
          <motion.path
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            d="M3 12h18"
            variants={pathVariants}
            custom={1}
            animate={isOpen ? 'open' : 'closed'}
          />
          <motion.path
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            d={isOpen ? 'M3 12h18' : 'M10 18h11'}
            variants={pathVariants}
            custom={2}
            animate={isOpen ? 'open' : 'closed'}
          />
        </svg>
      </FancyButton>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 top-0 md:!top-[-17px] h-[100svh] md:h-[102svh] !pt-28 md:!pt-48 bg-[#ececec] dark:bg-[#333c51] -z-10 flex flex-col font-extrabold uppercase overflow-hidden"
            style={{
              fontFamily: 'Inter, sans-serif',
              padding: '7rem 1.55rem 0px',
              transformOrigin: 'center top',
              width: '102vw',
              height: '102vh',
              left: '50%',
            }}
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <div className="w-full max-w-7xl mx-auto p-2 md:p-9 md:pr-4 flex flex-wrap gap-8 md:gap-12 justify-between max-h-[45svh] overflow-y-auto">
              <div className="w-full md:w-[45%] fade-in-bottom">
                <div className="mb-2 md:mb-4">
                  <Link
                    className={className}
                    // to={configuration.paths.appHome}
                    to="/"
                    onClick={toggleMenu}
                  >
                    <Slash /> Start New Chat <Dot />
                  </Link>
                </div>
                {Object.values(links).map((item: any) => (
                  <div key={item.path} className="mb-2 md:mb-4">
                    <Link
                      className={className}
                      to={item.path}
                      onClick={toggleMenu}
                    >
                      <Slash /> {item.label} <Dot />
                    </Link>
                  </div>
                ))}
              </div>
              <ChatBotWidget
                viewMode="card"
                insideNavigation
                containerClassName="hidden sm:flex w-full md:w-[45%] fade-in-bottom delay-150"
                cardClassName="my-16 !bg-white dark:!bg-[#3f4961] !shadow-sm !my-8"
              />
            </div>
            <div className="w-[80rem] max-w-full mx-auto p-2 px-6 lg:px-8 md:p-9 absolute bottom-8 left-1/2 transform -translate-x-1/2">
              <FooterCopyright className="normal-case font-medium text-sm md:text-base fade-in-bottom delay-300" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FullscreenMenu;
